import dayjs from "dayjs";
import type {
  CreditLog,
  CreditLogCategory,
  CreditLogStatus,
} from "@/lib/types/credit.types";

/**
 * Formatting helpers shared by the top-bar balance badge and the usage screen.
 */

const creditFormatter = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 2,
});

/** 1234.5 -> "1,234.5". Nullish values render as "0". */
export function formatCredits(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "0";
  return creditFormatter.format(value);
}

/** Signed amount for a ledger row: "+50" for credit, "-50" for debit. */
export function formatSignedAmount(log: CreditLog): string {
  const sign = log.direction === "credit" ? "+" : "-";
  return `${sign}${formatCredits(log.amount)}`;
}

/** "infyndEmail" -> "Infynd Email"; "creditAdded" -> "Credit Added". */
export function formatServiceType(type: string): string {
  if (!type) return "—";
  return type
    .replace(/[_-]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^./, (char) => char.toUpperCase());
}

export function formatDateTime(value: string | Date | null | undefined): string {
  if (!value) return "—";
  const date = dayjs(value);
  return date.isValid() ? date.format("DD MMM YYYY, hh:mm A") : "—";
}

export function formatDate(value: string | Date | null | undefined): string {
  if (!value) return "—";
  const date = dayjs(value);
  return date.isValid() ? date.format("DD MMM YYYY") : "—";
}

/**
 * Badge styling per outbox status. "pending" means the intent is written but
 * the balance update has not settled yet; "failed" means it never applied.
 */
export const CREDIT_STATUS_STYLES: Record<CreditLogStatus, string> = {
  applied:
    "border-transparent bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300",
  pending:
    "border-transparent bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300",
  failed:
    "border-transparent bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300",
};

export const CREDIT_STATUS_LABELS: Record<CreditLogStatus, string> = {
  applied: "Applied",
  pending: "Pending",
  failed: "Failed",
};

/**
 * Human wording per ledger category. This is what turns the usage table from a
 * raw credit/debit list into something a customer can read: the same "+500"
 * means "Free trial" on one row and "Refund" on another.
 */
export const CREDIT_CATEGORY_LABELS: Record<CreditLogCategory, string> = {
  creditAdded: "Credit purchased",
  bonusCredit: "Free trial",
  refund: "Refund",
  // Named after what they UNDO, so their names read backwards: creditReversal
  // reverses a charge and puts credit back, debitReversal reverses a credit and
  // takes it away. Labelled by what the customer actually sees happen, because
  // "credit reversed" on a row that ADDED credit is worse than no label at all.
  creditReversal: "Charge reversed",
  debit: "Usage",
  fee: "Fee",
  expiry: "Expired",
  debitReversal: "Credit withdrawn",
  adjustment: "Adjustment",
  teamAllocation: "From team pool",
  teamReclaim: "Returned to team pool",
};

/**
 * Falls back to a de-camel-cased version of an unrecognised category, so a
 * category added to the credit service before this map is updated still renders
 * as words rather than as nothing.
 */
export function formatCreditCategory(category: string): string {
  return CREDIT_CATEGORY_LABELS[category as CreditLogCategory] ?? formatServiceType(category);
}

/**
 * Row wording: prefer the category, since that is the part a customer
 * understands, and keep the service type as the secondary detail.
 *
 * `detail` comes back empty when the service type is only the category restated
 * — a purchase is logged with type "creditAdded" AND category "creditAdded", and
 * stacking "Credit purchased" over "Credit Added" reads as two different things
 * having happened. Only a type that names an actual service ("infyndEmail")
 * earns the second line.
 */
export function describeCreditLog(log: CreditLog): { label: string; detail: string } {
  const label = formatCreditCategory(log.category);
  const isTypeJustTheCategory =
    !log.type || log.type.toLowerCase() === String(log.category).toLowerCase();

  if (isTypeJustTheCategory) return { label, detail: "" };

  const detail = formatServiceType(log.type);
  return { label, detail: detail === label ? "" : detail };
}
